import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import { PERCENT, ZERO } from 'constants/number'
import CoreModal from 'core/CoreModal'
import { AssetInfo } from 'interfaces/contracts/edgePool'
import RiskRatioBar from 'components/RiskRatioBar'
import { AssetData } from 'constants/assets'
import { toAssetInfo } from 'utils/contractParse'
import { toA } from 'utils/display'
import ValueChange from 'components/container/ValueChange'
import ModalTextSpacing from 'components/container/ModalTextSpacing'
import { observer } from 'mobx-react-lite'
import { poolService } from 'services/poolService'
import { poolStore } from 'stores'
import { useMemo } from 'react'
import { ForceMaxRatio } from 'constants/constants'

interface Props {
  onClose: () => void
  change: BigNumber
  sendTx: (asset: AssetInfo) => void
  assetData: AssetData
}

const BoostBorrowingPowerModal = observer(
  ({ onClose, change, sendTx, assetData }: Props) => {
    const poolAddr = poolService.poolAddress
    const {
      walletBorrowingPowerUSD,
      walletTotalDebtValueUSD,
      walletCollateralValueUSD,
    } = poolStore.getMarketOverall(poolAddr, assetData.terraAddress)
    const borrowingPowerUSD = walletBorrowingPowerUSD || ZERO
    const nBorrowingPowerUSD = BigNumber.max(
      borrowingPowerUSD.plus(change),
      ZERO,
    )
    const newLTV = useMemo(() => {
      if (!walletTotalDebtValueUSD || !walletCollateralValueUSD) return ZERO
      if (walletCollateralValueUSD.eq(ZERO)) return ZERO
      if (walletTotalDebtValueUSD.eq(ZERO)) return ZERO
      if (
        walletCollateralValueUSD
          .plus(change)
          .div(walletTotalDebtValueUSD)
          .times(PERCENT)
          .lte(0.01)
      )
        return new BigNumber(999.99)
      const ratio = walletTotalDebtValueUSD
        .div(walletCollateralValueUSD.plus(change))
        .times(PERCENT)

      return ratio.gt(999.99) ? new BigNumber(999.99) : ratio
    }, [change, walletCollateralValueUSD, walletTotalDebtValueUSD])

    const handleOnConfirm = () => {
      sendTx(toAssetInfo(assetData.terraAddress))
      onClose()
    }

    const Content = (
      <Box>
        <Typography variant="h5" mb="8px">
          {change.lt(ZERO) ? 'Remove collateral' : 'Boost borrowing power'}
        </Typography>
        <Typography variant="body2" mb="24px" paragraph>
          {change.lt(ZERO)
            ? 'Your borrowing power will be decreased. Please carefully consider your risk ratio to avoid your position being liquidated.'
            : 'Using this asset as collateral will increase your borrowing power. Please note that this asset might be seized in liquidation.'}
        </Typography>
        {!change.eq(ZERO) && (
          <>
            <ModalTextSpacing
              itl={
                <Typography variant="body2">Borrowing power left</Typography>
              }
              itr={
                <ValueChange
                  bf={`$${toA(walletBorrowingPowerUSD)}`}
                  af={`$${toA(nBorrowingPowerUSD)}`}
                />
              }
              mb="16px"
            />
            <RiskRatioBar RiskRatio={newLTV} title="Risk ratio" showFullText />
          </>
        )}
        <Box sx={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
          <Button variant="outlined" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOnConfirm}
            fullWidth
            disabled={newLTV.gt(ForceMaxRatio) && !change.eq(ZERO)}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    )

    return <CoreModal open content={Content} onClose={onClose} />
  },
)

export default BoostBorrowingPowerModal
